export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url)
    return true
  } catch (err) {
    return false
  }
}

/**
 * Guards against open-redirect attacks by only allowing a returnUrl that is
 * either a same-app relative path, or an http(s) URL on `currentHost`.
 * Protocol-relative ("//evil.com") and backslash ("/\evil.com") tricks that
 * browsers can treat as absolute are rejected. ASCII tab/newline/CR are
 * stripped first since the URL spec strips them before parsing too — otherwise
 * "/\t/evil.com" looks like a safe relative path here but resolves to
 * https://evil.com once the browser parses it. The scheme is checked
 * explicitly (not just the host) because `new URL()` still assigns a `host`
 * to non-http schemes that use "//" authority syntax — e.g.
 * "javascript://<currentHost>/%0aalert(1)" has `host === currentHost` but,
 * if allowed through to `window.location.href`, executes as script rather
 * than navigating. The host comparison is case-insensitive (host casing
 * carries no meaning per RFC 3986/DNS) but the returned value is always the
 * original `stripped` string rather than a re-serialized URL, so path/query
 * casing — which some tenant apps depend on — is never altered.
 */
export const sanitizeReturnUrl = (
  returnUrl: string | null | undefined,
  currentHost?: string,
  fallback = '/',
): string => {
  if (!returnUrl) {
    return fallback
  }

  const stripped = returnUrl.replace(/[\t\r\n]/g, '')
  const normalized = stripped.replace(/\\/g, '/')

  if (normalized.startsWith('/')) {
    return normalized.startsWith('//') ? fallback : normalized
  }

  if (!currentHost) {
    return fallback
  }

  try {
    const parsed = new URL(stripped)
    const isHttpScheme =
      parsed.protocol === 'http:' || parsed.protocol === 'https:'
    const isSameHost = parsed.host.toLowerCase() === currentHost.toLowerCase()

    return isHttpScheme && isSameHost ? stripped : fallback
  } catch {
    return fallback
  }
}

export const getGreeting = (): string => {
  const hour = new Date().getHours()

  if (hour < 12) return 'morning'
  if (hour < 18) return 'afternoon'
  return 'evening'
}

export const safeParse = (value: unknown) => {
  if (typeof value !== 'string') return undefined
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}
